'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { ArrowRight, Check, X, ChevronDown, Languages } from 'lucide-react';

import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { FAQ } from '@/lib/legal/documents';
import { track } from '@/lib/analytics/track';
import {
  CLOSE,
  DEMO,
  EARLY,
  EXAMPLES,
  FAQ_TITLE,
  FEATURES,
  HERO,
  PHONE,
  PRICING,
  PRIVACY_BLOCK,
  PROBLEM,
  STEPS,
  type Copy,
} from '@/lib/marketing/landing';

/**
 * The page a stranger lands on.
 *
 * It carries the app's own tokens rather than a palette of its own, so what
 * somebody sees before signing up is what they get after — the screenshots sit
 * on the same canvas colour as the page around them.
 *
 * Signed-in visitors are not redirected away. The page stays static and
 * cacheable, and the one thing that changes for them is the button, which says
 * "go to my dashboard" instead of "create an account".
 */

function Shot({ src, alt, priority = false }: { src: string; alt: string; priority?: boolean }) {
  return (
    <div className="overflow-hidden rounded-xl border border-hairline-strong bg-surface shadow-raised">
      {/* Plain <img>: these are fixed-size WebP already sized for their slot,
          so the image pipeline would add a request and a layout shift for
          nothing. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className="block h-auto w-full"
      />
    </div>
  );
}

export default function Landing() {
  const { language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const isEs = language === 'es';
  const say = (c: Copy) => (isEs ? c.es : c.en);

  // Counted once per tab. Someone who already has an account is not a visitor
  // arriving, and counting them would quietly inflate the top of the funnel.
  useEffect(() => {
    if (!user) track('landing_view');
  }, [user]);

  const signedIn = !!user;
  const ctaHref = signedIn ? '/dashboard' : '/login?mode=signup';
  const ctaLabel = say(signedIn ? HERO.ctaSignedIn : HERO.cta);

  const Cta = ({ className = '' }: { className?: string }) => (
    <Link
      href={ctaHref}
      onClick={() => {
        if (!signedIn) track('cta_click');
      }}
      className={`btn-primary min-h-[52px] px-7 text-[15.5px] ${className}`}
    >
      {ctaLabel}
      <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
    </Link>
  );

  return (
    <div className="min-h-screen bg-canvas">
      {/* ── Barra ────────────────────────────────────────────────── */}
      <header className="border-b border-hairline">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 pt-[calc(1rem+env(safe-area-inset-top))] sm:px-8 sm:pt-4">
          <span className="flex items-center gap-2.5">
            <AppLogo size={28} />
            <span className="font-display text-[17px] font-semibold tracking-tight text-ink">
              Quantifyr
            </span>
          </span>

          <div className="flex items-center gap-1 sm:gap-3">
            <button
              type="button"
              onClick={() => void setLanguage(isEs ? 'en' : 'es')}
              className="flex min-h-[44px] items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-ink-faint transition-colors hover:text-ink"
              aria-label={isEs ? 'Switch to English' : 'Cambiar a español'}
            >
              <Languages className="h-4 w-4" strokeWidth={2} />
              {isEs ? 'EN' : 'ES'}
            </button>
            <Link
              href="/login"
              className="flex min-h-[44px] items-center px-2.5 text-[13.5px] font-medium text-ink-soft transition-colors hover:text-ink"
            >
              {isEs ? 'Iniciar sesión' : 'Sign in'}
            </Link>
            <Link href={ctaHref} className="btn-primary hidden min-h-[44px] sm:inline-flex">
              {ctaLabel}
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* ── Héroe ──────────────────────────────────────────────── */}
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-[-30%] h-[700px] w-[min(1000px,130vw)] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,hsl(var(--brand)/0.10)_0%,transparent_62%)]"
          />
          <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
            <h1 className="max-w-[16ch] whitespace-pre-line font-display text-[2.4rem] font-bold leading-[1.05] tracking-tight text-ink sm:text-[3.6rem]">
              {say(HERO.title)}
            </h1>
            <p className="mt-6 max-w-[58ch] text-[17px] leading-relaxed text-ink-soft sm:text-[19px]">
              {say(HERO.sub)}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Cta />
              <Link href="/presentacion" className="btn-secondary min-h-[52px] px-6 text-[15px]">
                {isEs ? 'Ver la presentación' : 'See the walkthrough'}
              </Link>
            </div>
            <p className="mt-4 text-[13px] text-ink-ghost">{say(HERO.note)}</p>

            <div className="mt-14 sm:mt-16">
              <Shot src="/marketing/dashboard.webp" alt={say(FEATURES[0].shotAlt)} priority />
            </div>
          </div>
        </section>

        {/* ── Problema ───────────────────────────────────────────── */}
        <Section eyebrow={say(PROBLEM.eyebrow)} title={say(PROBLEM.title)} lede={say(PROBLEM.lede)}>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {PROBLEM.points.map((p) => (
              <div key={p.title.en} className="card min-w-0 p-6">
                <h3 className="font-display text-[16px] font-semibold tracking-tight text-ink">
                  {say(p.title)}
                </h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-ink-faint">{say(p.body)}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Pasos ──────────────────────────────────────────────── */}
        <Section eyebrow={say(STEPS.eyebrow)} title={say(STEPS.title)}>
          <ol className="mt-10 border-t border-hairline">
            {STEPS.items.map((s, i) => (
              <li
                key={s.title.en}
                className="grid grid-cols-[auto_1fr] gap-5 border-b border-hairline py-7"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-brand-edge bg-brand-wash font-display text-[13px] font-bold text-brand-bright">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-[17px] font-semibold tracking-tight text-ink">
                    {say(s.title)}
                  </h3>
                  <p className="mt-1.5 max-w-[62ch] text-[15px] leading-relaxed text-ink-faint">
                    {say(s.body)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        {/* ── Funciones ──────────────────────────────────────────── */}
        {FEATURES.map((f) => (
          <Section key={f.shot} eyebrow={say(f.eyebrow)} title={say(f.title)} narrow>
            <div
              className={`mt-10 grid items-center gap-8 lg:grid-cols-2 lg:gap-12 ${
                f.reverse ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              <div className="min-w-0 space-y-4">
                {f.body.map((b, i) => (
                  <p key={i} className="max-w-[58ch] text-[15.5px] leading-relaxed text-ink-soft">
                    {say(b)}
                  </p>
                ))}
              </div>
              <div className="min-w-0">
                <Shot src={f.shot} alt={say(f.shotAlt)} />
              </div>
            </div>
          </Section>
        ))}

        {/* ── Teléfono ───────────────────────────────────────────── */}
        <Section eyebrow={say(PHONE.eyebrow)} title={say(PHONE.title)} lede={say(PHONE.lede)}>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {PHONE.shots.map((s) => (
              <figure key={s.src} className="min-w-0">
                <Shot src={s.src} alt={say(s.alt)} />
                <figcaption className="mt-3 text-[13px] text-ink-ghost">{say(s.cap)}</figcaption>
              </figure>
            ))}
          </div>
        </Section>

        {/* ── Demostración ───────────────────────────────────────── */}
        <Section eyebrow={say(DEMO.eyebrow)} title={say(DEMO.title)} lede={say(DEMO.lede)}>
          <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline sm:grid-cols-4">
            {DEMO.stats.map((s) => (
              <div key={s.l.en} className="min-w-0 bg-surface px-5 py-5">
                <b className="numeric block font-display text-[1.7rem] font-bold tracking-tight text-ink">
                  {s.n}
                </b>
                <span className="mt-0.5 block text-[12.5px] text-ink-ghost">{say(s.l)}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 max-w-[62ch] text-[15px] leading-relaxed text-ink-soft">
            {say(DEMO.closing)}
          </p>
        </Section>

        {/* ── Ejemplos por giro ──────────────────────────────────── */}
        <Section
          eyebrow={say(EXAMPLES.eyebrow)}
          title={say(EXAMPLES.title)}
          lede={say(EXAMPLES.lede)}
        >
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {EXAMPLES.items.map((e) => (
              <div key={e.trade.en} className="card min-w-0 p-6">
                <h3 className="font-display text-[16px] font-semibold tracking-tight text-ink">
                  {say(e.trade)}
                </h3>
                <dl className="mt-3 space-y-2.5">
                  <div>
                    <dt className="eyebrow">{isEs ? 'Registra' : 'Records'}</dt>
                    <dd className="mt-0.5 text-[14px] leading-relaxed text-ink-faint">
                      {say(e.tracks)}
                    </dd>
                  </div>
                  <div>
                    <dt className="eyebrow">{isEs ? 'Vigila' : 'Watches'}</dt>
                    <dd className="mt-0.5 text-[14px] leading-relaxed text-ink-faint">
                      {say(e.watches)}
                    </dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Etapa temprana ─────────────────────────────────────── */}
        <Section eyebrow={say(EARLY.eyebrow)} title={say(EARLY.title)} narrow>
          <p className="mt-6 max-w-[62ch] text-[16px] leading-relaxed text-ink-soft">
            {say(EARLY.body)}
          </p>
          <ul className="mt-7 space-y-3">
            {EARLY.points.map((p) => (
              <li key={p.en} className="grid grid-cols-[20px_1fr] gap-3">
                <Check className="mt-1 h-4 w-4 text-brand" strokeWidth={2.4} />
                <span className="text-[15px] leading-relaxed text-ink-soft">{say(p)}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── Precio ─────────────────────────────────────────────── */}
        <Section eyebrow={say(PRICING.eyebrow)} title={say(PRICING.title)}>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <Plan plan={PRICING.free} say={say} />
            <Plan plan={PRICING.paid} say={say} featured />
          </div>
          <p className="mt-7 max-w-[60ch] text-[14.5px] leading-relaxed text-ink-faint">
            {say(PRICING.note)}
          </p>
          <div className="mt-8">
            <Cta />
          </div>
        </Section>

        {/* ── Privacidad ─────────────────────────────────────────── */}
        <Section eyebrow={say(PRIVACY_BLOCK.eyebrow)} title={say(PRIVACY_BLOCK.title)}>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {PRIVACY_BLOCK.points.map((p) => (
              <div key={p.title.en} className="card min-w-0 p-6">
                <h3 className="font-display text-[15.5px] font-semibold tracking-tight text-ink">
                  {say(p.title)}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-ink-faint">{say(p.body)}</p>
              </div>
            ))}
          </div>
          <p className="mt-7 max-w-[62ch] text-[14.5px] leading-relaxed text-ink-faint">
            {say(PRIVACY_BLOCK.link)}{' '}
            <Link href="/privacy" className="text-brand-bright underline underline-offset-2">
              {isEs ? 'Leer el aviso de privacidad' : 'Read the privacy notice'}
            </Link>
          </p>
        </Section>

        {/* ── Preguntas ──────────────────────────────────────────── */}
        <Section eyebrow={say(FAQ_TITLE.eyebrow)} title={say(FAQ_TITLE.title)} narrow>
          <div className="mt-8 divide-y divide-hairline border-b border-hairline">
            {FAQ.slice(0, 6).map((item) => (
              <details key={item.q.en} className="group">
                <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 py-4 text-[15.5px] font-medium text-ink [&::-webkit-details-marker]:hidden">
                  {say(item.q)}
                  <ChevronDown
                    className="h-4 w-4 flex-shrink-0 text-ink-ghost transition-transform duration-200 group-open:rotate-180"
                    strokeWidth={2.1}
                  />
                </summary>
                <p className="max-w-[62ch] pb-5 pr-8 text-[15px] leading-relaxed text-ink-soft">
                  {say(item.a)}
                </p>
              </details>
            ))}
          </div>
          <Link
            href="/faq"
            className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 text-[14px] font-medium text-brand-bright"
          >
            {say(FAQ_TITLE.more)}
            <ArrowRight className="h-4 w-4" strokeWidth={2.1} />
          </Link>
        </Section>

        {/* ── Cierre ─────────────────────────────────────────────── */}
        <section className="border-t border-hairline">
          <div className="mx-auto max-w-6xl px-5 py-20 text-center sm:px-8 sm:py-28">
            <h2 className="mx-auto max-w-[20ch] font-display text-[1.9rem] font-bold leading-tight tracking-tight text-ink sm:text-[2.6rem]">
              {say(CLOSE.title)}
            </h2>
            <p className="mx-auto mt-5 max-w-[54ch] text-[16.5px] leading-relaxed text-ink-soft">
              {say(CLOSE.lede)}
            </p>
            <div className="mt-9 flex justify-center">
              <Cta />
            </div>
            <p className="mt-4 text-[13px] text-ink-ghost">{say(HERO.note)}</p>
          </div>
        </section>
      </main>

      <footer className="border-t border-hairline">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-8 text-[13px] text-ink-ghost sm:px-8">
          <span>© {new Date().getFullYear()} Quantifyr</span>
          <span className="flex flex-wrap gap-x-5 gap-y-2">
            <Link href="/presentacion" className="hover:text-ink-faint">
              {isEs ? 'Presentación' : 'Walkthrough'}
            </Link>
            <Link href="/faq" className="hover:text-ink-faint">
              {isEs ? 'Preguntas' : 'FAQ'}
            </Link>
            <Link href="/terms" className="hover:text-ink-faint">
              {isEs ? 'Términos' : 'Terms'}
            </Link>
            <Link href="/privacy" className="hover:text-ink-faint">
              {isEs ? 'Privacidad' : 'Privacy'}
            </Link>
          </span>
        </div>
      </footer>
    </div>
  );
}

/** Section chrome, so every band on the page shares one rhythm. */
function Section({
  eyebrow,
  title,
  lede,
  narrow = false,
  children,
}: {
  eyebrow: string;
  title: string;
  lede?: string;
  narrow?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-hairline">
      <div className={`mx-auto px-5 py-16 sm:px-8 sm:py-24 ${narrow ? 'max-w-4xl' : 'max-w-6xl'}`}>
        <p className="eyebrow text-brand">{eyebrow}</p>
        <h2 className="mt-3 max-w-[22ch] text-balance font-display text-[1.75rem] font-bold leading-[1.12] tracking-tight text-ink sm:text-[2.4rem]">
          {title}
        </h2>
        {lede && (
          <p className="mt-5 max-w-[62ch] text-[16px] leading-relaxed text-ink-soft sm:text-[17px]">
            {lede}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}

function Plan({
  plan,
  say,
  featured = false,
}: {
  plan: {
    name: Copy;
    price: string;
    per?: Copy;
    items: Array<{ on: boolean; t: Copy }>;
  };
  say: (c: Copy) => string;
  featured?: boolean;
}) {
  return (
    <div
      className={`min-w-0 rounded-2xl border p-7 ${
        featured
          ? 'border-brand-edge bg-gradient-to-b from-brand-wash to-transparent'
          : 'border-hairline bg-surface'
      }`}
    >
      <h3 className="font-display text-[16px] font-semibold tracking-tight text-ink">
        {say(plan.name)}
      </h3>
      <p className="mt-1.5 font-display text-[2.2rem] font-bold tracking-tight text-ink">
        {plan.price}
        {plan.per && (
          <span className="ml-2 text-[14px] font-medium text-ink-ghost">{say(plan.per)}</span>
        )}
      </p>
      <ul className="mt-6 space-y-2.5">
        {plan.items.map((it) => (
          <li key={it.t.en} className="grid grid-cols-[18px_1fr] gap-2.5">
            {it.on ? (
              <Check className="mt-1 h-[15px] w-[15px] text-brand" strokeWidth={2.4} />
            ) : (
              <X className="mt-1 h-[15px] w-[15px] text-ink-ghost" strokeWidth={2.4} />
            )}
            <span
              className={`text-[14.5px] leading-relaxed ${it.on ? 'text-ink-soft' : 'text-ink-ghost'}`}
            >
              {say(it.t)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
